import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryDown } from 'components/utils/breakpoint'
import focusStyle from 'styles/utils/focusStyle'

const Row = styled.div<{ $selected?: boolean }>`
  padding: ${rem(8)};
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: max-content max-content 1fr max-content;
  align-items: center;
  column-gap: ${rem(8)};

  @supports (grid-template-columns: subgrid) {
    grid-template-columns: subgrid;
  }

  &.clickable {
    border: 0;
    border-radius: ${props => props.theme.borderRadius.S};
    background-color: ${props => props.$selected ? props.theme.palette.neutral.surface.lightBackground : 'transparent'};
    box-shadow: ${props => props.$selected ? `inset 0 0 0 ${rem(1)} ${props.theme.palette.brand.primary.normal}` : 'none'};
    color: inherit;
    cursor: pointer;
    font: inherit;
    text-align: inherit;

    &:focus-visible {
      ${focusStyle}
    }

    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  ${mediaQueryDown.mobile} {
    padding: ${rem(4)} 0;
  }

  [dir='rtl'] & {
    direction: ltr;

    > * {
      direction: rtl;
    }
  }
`

const Container = styled(Group)<{ $selected?: boolean }>`
  padding: ${rem(8)};

  &.clickable {
    border: 0;
    border-radius: ${props => props.theme.borderRadius.S};
    background-color: ${props => props.$selected ? props.theme.palette.neutral.surface.lightBackground : 'transparent'};
    box-shadow: ${props => props.$selected ? `inset 0 0 0 ${rem(1)} ${props.theme.palette.brand.primary.normal}` : 'none'};
    color: inherit;
    cursor: pointer;
    font: inherit;
    text-align: inherit;

    &:focus-visible {
      ${focusStyle}
    }

    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  ${mediaQueryDown.mobile} {
    padding: ${rem(4)} 0;
  }
`

const PanelHeader = styled.div<{ $width: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: ${props => rem(props.$width)};
  white-space: nowrap;

  [dir='rtl'] & {
    direction: ltr;

    > * {
      direction: rtl;
    }
  }
`

const PanelLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const ProgressBar = styled.div<{ $width?: number }>`
  width: ${props => rem(props.$width || 190)};
  height: ${rem(6)};
  background: linear-gradient(
    to right,
    ${props => props.theme.palette.brand.primary.normal} 0% var(--progress, 0%),
    ${props => props.theme.palette.neutral.surface.lightForeground} var(--progress, 0%) 100%
  );
  border-radius: ${props => props.theme.borderRadius.round};

  [dir='rtl'] & {
    background: linear-gradient(
      to left,
      ${props => props.theme.palette.brand.primary.normal} 0% var(--progress, 0%),
      ${props => props.theme.palette.neutral.surface.lightForeground} var(--progress, 0%) 100%
    );
  }
`

interface Props {
  label: string
  range: string
  progress: number
  count: number
  isPanel?: boolean
  width?: number
  textVariant?: 'small' | 'medium'
  onClick?: () => void
  selected?: boolean
}

function RatingDistribution({ label, range, progress, count, isPanel = false, width = 190, textVariant = 'medium', onClick, selected }: Props) {
  const intl = useIntl()
  const clickable = !!onClick

  const labelWithColon = intl.formatMessage(
    {
      id: 'T68LykwDOMKfrUFeHpu-',
      defaultMessage: '{label}:',
    },
    { label },
  )

  if (isPanel) {
    return (
      <Container
        as={clickable ? 'button' : 'div'}
        type={clickable ? 'button' : undefined}
        direction="vertical"
        verticalAlign="center"
        gap={4}
        className={clickable ? 'clickable' : undefined}
        onClick={onClick}
        aria-pressed={clickable ? selected : undefined}
        $selected={selected}
      >
        <PanelHeader $width={width}>
          <PanelLabelGroup>
            <BodyText variant="medium" weight="bold">{labelWithColon}</BodyText>
            <BodyText variant="small">{range}</BodyText>
          </PanelLabelGroup>
          <BodyText variant="medium">{count}</BodyText>
        </PanelHeader>
        <ProgressBar $width={width} style={{ '--progress': `${progress}%` } as React.CSSProperties} />

      </Container>
    )
  }

  return (
    <Row
      as={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      className={clickable ? 'clickable' : undefined}
      onClick={onClick}
      aria-pressed={clickable ? selected : undefined}
      $selected={selected}
    >
      <BodyText variant={textVariant} weight="bold">{labelWithColon}</BodyText>
      <BodyText variant={textVariant}>{range}</BodyText>
      <ProgressBar style={{ '--progress': `${progress}%` } as React.CSSProperties} />
      <BodyText variant={textVariant}>{count}</BodyText>
    </Row>
  )
}

export default RatingDistribution
